import { ReactElement, useMemo, useState } from 'react'
import { Box, FormControlLabel, Stack, Switch } from '@mui/material'
import { Section } from '@ui2/structure'
import { Form2, TextField } from '@ui/forms'
import { useEditorViewStore } from '@editorStores'
import { PLAN_NAME_MAX_LENGTH, PlanViewPlanChoice } from '../constants'
import useGeneratePlan, { NewPlanMetadata } from '../hooks/useGeneratePlan'
import { getDefaultPlanName, getTodayHandInDate } from '../utils'
import FormTagsField from './FormTagsField'
import PlanExportButton from './PlanExportButton'
import { newPlanSchema } from './newPlanSchema'

interface Props {
  storeys: readonly string[]
  planChoice: PlanViewPlanChoice
  /** Plan settings changes not saved yet: generating would still use the old settings. */
  isPlanSettingsSavePending: boolean
}

/** A new default name (another plan type or storey) replaces the name; the rest is kept. */
const reinitialize = (current: NewPlanMetadata, defaults: NewPlanMetadata): NewPlanMetadata => ({
  ...current,
  name: defaults.name,
})

/**
 * "Erstellen" section: name, tags and comment of the plan to generate, then generates it
 * (optionally downloading it once ready). The name starts as the default for the chosen plan type
 * and storeys, the comment as today's hand-in date (as plans generated before got it). The fields
 * are locked while the plan is generated and reset for the next plan once it is saved.
 */
const PlanViewCreateSection = ({
  storeys,
  planChoice,
  isPlanSettingsSavePending,
}: Props): ReactElement => {
  const downloadAfterGenerate = useEditorViewStore(state => state.planView.downloadAfterGenerate)
  const setDownloadAfterGenerate = useEditorViewStore(
    state => state.setPlanViewDownloadAfterGenerate,
  )
  const { generatePlan, isGenerating, hasResults } = useGeneratePlan(storeys, planChoice)
  // Bumped after a plan was created: remounts the form with fresh default values.
  const [formKey, setFormKey] = useState(0)

  const defaultName = getDefaultPlanName(planChoice, storeys)
  const defaultValues = useMemo<NewPlanMetadata>(
    () => ({ name: defaultName, comment: getTodayHandInDate(), tags: [] }),
    [defaultName],
  )

  const onSubmit = async ({ name, comment, tags }: NewPlanMetadata) => {
    if (await generatePlan({ name: name.trim(), comment, tags })) setFormKey(key => key + 1)
  }

  return (
    <Section title="Erstellen" hideBorderBottom>
      <Form2
        key={formKey}
        defaultValues={defaultValues}
        reinitialize={reinitialize}
        validationSchema={newPlanSchema}
        mode="onChange"
        onSubmit={onSubmit}
        data-cy="plan-view-new-plan-form"
      >
        <Stack spacing={1} pt={1}>
          <TextField
            name="name"
            inputLabel="Planname"
            inputProps={{ maxLength: PLAN_NAME_MAX_LENGTH }}
            disabled={isGenerating}
            fullWidth
            data-cy="plan-view-new-plan-name"
          />
          <FormTagsField name="tags" disabled={isGenerating} />
          <Box pt={1}>
            <TextField
              name="comment"
              inputLabel="Kommentar"
              placeholder="Kommentar"
              disabled={isGenerating}
              multiline
              rows={2}
              fullWidth
              data-cy="plan-view-new-plan-comment"
            />
          </Box>
          {/* One row; wraps (button below the switch) only when the drawer is too narrow. */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'flex-end',
              columnGap: 2,
              rowGap: 1,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={downloadAfterGenerate}
                  onChange={event => setDownloadAfterGenerate(event.target.checked)}
                />
              }
              label="Plan herunterladen"
              labelPlacement="start"
              sx={{ mr: 0 }}
              data-cy="plan-view-download-after-generate-switch"
            />
            <PlanExportButton
              storeys={storeys}
              planType={planChoice}
              isGenerating={isGenerating}
              hasResults={hasResults}
              isPlanSettingsSavePending={isPlanSettingsSavePending}
            />
          </Box>
        </Stack>
      </Form2>
    </Section>
  )
}

export default PlanViewCreateSection
